import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { useLineItemImages } from '../../hooks/useLineItemImages';
import { useLineItems } from '../../hooks/useLineItems';
import { ImageThumbnailCard, ImageUploadCard, ImagePreviewModal } from './imageViewer';

interface ImageViewerDialogProps {
  imageHook: ReturnType<typeof useLineItemImages>;
  lineItemsHook: ReturnType<typeof useLineItems>;
}

export const ImageViewerDialog: React.FC<ImageViewerDialogProps> = ({
  imageHook,
  lineItemsHook
}) => {
  const { 
    imageViewerVisible, 
    selectedLineItemImages, 
    isLoading, 
    error, 
    uploadImages,
    deleteImage,
    isUploading,
    deletingImages,
    refetch,
    onHide
  } = imageHook;

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const toastRef = useRef<Toast>(null);

  // Track previous states to detect changes
  const [prevError, setPrevError] = useState<string | null>(null);
  const [prevImageCount, setPrevImageCount] = useState(0);
  const [prevDeletingCount, setPrevDeletingCount] = useState(0);

  // Monitor for successful operations and errors
  useEffect(() => {
    // Handle error changes
    if (error && error !== prevError) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 5000
      });
      setPrevError(error);
    }

    // Handle successful image deletion
    const currentDeletingCount = deletingImages.size;
    const currentImageCount = selectedLineItemImages.length;
    
    // If we were deleting images and now we're not, and image count decreased
    if (prevDeletingCount > 0 && currentDeletingCount === 0 && currentImageCount < prevImageCount) {
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Image deleted successfully',
        life: 3000
      });
    }

    // Update previous states
    setPrevImageCount(currentImageCount);
    setPrevDeletingCount(currentDeletingCount);
  }, [error, selectedLineItemImages.length, deletingImages.size, isUploading, prevError, prevImageCount, prevDeletingCount]);

  // Reset previous states when dialog opens/closes
  useEffect(() => {
    if (imageViewerVisible) {
      setPrevError(null);
      setPrevImageCount(selectedLineItemImages.length);
      setPrevDeletingCount(0);
    }
  }, [imageViewerVisible, selectedLineItemImages.length]);

  const handleImageClick = useCallback((index: number) => {
    setPreviewIndex(index);
    setPreviewVisible(true);
  }, []);

  const handleFileUpload = useCallback(async (files: File[]) => {
    try {
      await uploadImages(files, lineItemsHook.selectedLineItem?.workflow_id, lineItemsHook.selectedLineItem?.order_id,
                        lineItemsHook.selectedLineItem?.line_item_id);
      // Refetch images after successful upload
      await refetch();
    } catch (error) {
      // Error already handled by the hook
    }
  }, [uploadImages, refetch, lineItemsHook.selectedLineItem]);

  const handleImageDelete = useCallback((id: number) => {
    deleteImage(id);
  }, [deleteImage]);

  const footer = (
    <div className="flex justify-content-end">
      <Button label="Close" icon="pi pi-times" onClick={onHide} />
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center p-6">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          <p className="text-600 mt-3">Loading images...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4">
          <Message severity="error" text={error} />
        </div>
      );
    }

    return (
      <div className="p-4">
        {/* Image Grid */}
        <div 
          className="grid gap-3"
          style={{
            display: 'grid', // Ensure grid display is set
            gridTemplateColumns: 'repeat(3, 1fr)', // 3 images per row 
            gap: '1rem', // Add some gap between items
            maxHeight: '60vh',
            overflowY: 'auto'
          }}
        >
          {/* Image Cards */}
          {selectedLineItemImages.map((image, index) => (
            <ImageThumbnailCard
              key={`${image.id}-${index}`}
              imageUrl={image.image_url}
              index={index}
              onDelete={() => handleImageDelete(image.id)}
              onClick={() => handleImageClick(index)}
              isDeleting={deletingImages?.has(image.id)}
            />
          ))}
        </div>

        {/* Empty State */}
        {selectedLineItemImages.length === 0 && !isUploading && (
          <div className="text-center p-6">
            <i className="pi pi-image text-6xl text-400 mb-4"></i>
            <h3 className="text-600 mb-2">No images yet</h3>
            <p className="text-500">Upload your first image using the card below</p>
          </div>
        )}
        {/* Separator */}
        {selectedLineItemImages.length > 0 && (
          <div className="my-4 border-top-1 border-300"></div>
        )}

        {/* Upload Card */}
        <ImageUploadCard 
            onUpload={handleFileUpload} 
            isUploading={isUploading}
          />
      </div>
    );
  };

  return (
    <>
      <Dialog
        header="Product Images"
        visible={imageViewerVisible}
        onHide={onHide}
        style={{ width: '90vw', maxWidth: '900px' }}
        modal
        footer={footer}
      >
        {renderContent()}
      </Dialog>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        visible={previewVisible}
        imageUrl={selectedLineItemImages[previewIndex]?.image_url || null}
        onHide={() => setPreviewVisible(false)}
      />

      <ConfirmDialog />
      <Toast ref={toastRef} />
    </>
  );
};